import mapboxgl from 'mapbox-gl';
import { Address, BuildingWithConvertedAddresses, POI } from '../utils/types';
import { resolveArea } from '../AppContent/NewLandingPage/CouldBuyFlow/neighbourhoodApi';
import { fetchPlaceLabels, HERE_MAX_M, pickDistrictRows } from './placeLabels';

/**
 * Text of the Mapbox context entry of `type` ('place', 'postcode', ...) on a
 * geocoder feature, or `fallback` when the feature has none. Callers that build
 * display strings pass '' so a missing part is not rendered as "Unknown".
 */
export const getContext = (type: string, place: any, fallback = 'Unknown') => {
  const context = place.context?.find((c: any) => c.id.startsWith(type));
  return context ? context.text : fallback;
};

// Whether a Mapbox `building` feature exists at a coordinate. Uses a hidden,
// throwaway map (modeled on the LandingPageCouldBuyOrCouldSellModal lookup) so
// the Add-my-place flow can block addresses with no building before advancing.
// Queries on `idle` (tiles rendered) over a small pixel box so an address point
// just off the footprint still counts; a safety timeout never hangs the flow.
export const buildingExistsAtCoord = (
  lngLat: { lng: number; lat: number },
): Promise<boolean> =>
  new Promise((resolve) => {
    let done = false;
    let container: HTMLElement | null = null;
    let map: mapboxgl.Map | null = null;
    const finish = (result: boolean) => {
      if (done) return;
      done = true;
      try {
        map?.remove();
      } catch {
        // ignore
      }
      try {
        if (container && document.body.contains(container)) {
          document.body.removeChild(container);
        }
      } catch {
        // ignore
      }
      resolve(result);
    };
    try {
      mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';
      container = document.createElement('div');
      container.style.cssText = 'width:300px;height:300px;visibility:hidden;position:absolute;left:-9999px;top:-9999px;';
      document.body.appendChild(container);
      map = new mapboxgl.Map({
        container,
        style: `mapbox://styles/jaakkocom/${process.env.REACT_APP_MAPBOX_STYLE_ID}`,
        center: [lngLat.lng, lngLat.lat],
        zoom: 18,
        interactive: false,
      });
      const timer = window.setTimeout(() => finish(false), 6000);
      map.on('idle', () => {
        window.clearTimeout(timer);
        const m = map;
        if (!m || !m.getLayer('building')) {
          finish(false);
          return;
        }
        const p = m.project([lngLat.lng, lngLat.lat]);
        // Match the building step's highlighter (`findBuildingFootprintAtCoord`,
        // R = 80) so this gate never rejects a point the building step would
        // have happily highlighted.
        const R = 80;
        const feats = m.queryRenderedFeatures(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          [[p.x - R, p.y - R], [p.x + R, p.y + R]] as any,
          { layers: ['building'] },
        );
        finish(feats.length > 0);
      });
      map.on('error', () => {
        window.clearTimeout(timer);
        finish(false);
      });
    } catch {
      finish(false);
    }
  });

export interface ReverseGeocodedAddress {
  country: string;
  region: string;
  city: string;
  postcode: string;
  street: string;
  houseNumber: string;
  coordinates: { lat: number; lng: number };
}

// AMP-scoped reverse geocode: resolve a coordinate to the precise civic address
// when possible, otherwise the nearest feature of any type. Kept separate from
// `performReverseGeocodingAndFetchPOIs` so the main building-page flow is
// untouched, and so it never leaves the caller without an address.
export const reverseGeocodeNearestAddress = async (
  lngLat: { lng: number; lat: number },
): Promise<ReverseGeocodedAddress | null> => {
  const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  const fetchAt = async (types?: string) => {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lngLat.lng},${lngLat.lat}.json?access_token=${token}${types ? `&types=${types}` : ''}`;
    try {
      const data = await fetch(url).then((r) => r.json());
      return data?.features?.[0] ?? null;
    } catch {
      return null;
    }
  };
  // Prefer a precise address; fall back to the nearest feature of any type.
  const place = (await fetchAt('address')) ?? (await fetchAt());
  if (!place) return null;
  // Use the resolved feature's own location (snapped to the address point)
  // rather than echoing the raw input coordinates, which may be off the
  // building footprint. Mapbox `center` is `[lng, lat]`; fall back to the
  // input coords if it's missing.
  const center = Array.isArray(place.center) && place.center.length === 2
    ? { lat: place.center[1], lng: place.center[0] }
    : { lat: lngLat.lat, lng: lngLat.lng };
  // Empty (not "Unknown") fallbacks so the joined "street houseNumber" label
  // stays clean when a piece is missing.
  return {
    country: getContext('country', place),
    region: getContext('region', place),
    city: getContext('place', place),
    postcode: getContext('postcode', place),
    street: place.text || '',
    houseNumber: place.address || '',
    coordinates: center,
  };
};

/**
 * The name and address a map click opens the building with, or null to keep it
 * closed (JAA-417). The geocoder's first feature gives the full street address,
 * exactly as master. With no feature and `allowDistrict` (plain buildings,
 * dots and cold links, never listing markers) the building opens
 * district-only: no street or house number, named after the nearest place
 * label within HERE_MAX_M, a kaupunginosa ('suburb') before a smaller quarter
 * (pickDistrictRows, as the search ranks them), else the OSM area the backend
 * resolves for the point (its OSM name, the same for every viewer, so a seller
 * and a buyer read one name), else the place a types=place reverse geocode
 * names. The three lookups run in parallel. The city is the OSM area, else
 * that Mapbox place, so a far Mapbox place never shows beside a nearer OSM area;
 * the region and country come from the Mapbox place. Only with no name at all
 * does it stay closed.
 */
// A stalled district provider must not hide names already returned by the others.
const settleMapLookup = async <T,>(request: Promise<T>): Promise<T | null> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      request,
      new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), 6000); }),
    ]);
  } catch {
    return null;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};

const resolveClickAddress = async (
  features: any[],
  lngLat: { lat: number; lng: number },
  accessToken: string | undefined,
  allowDistrict: boolean,
): Promise<{ name: string; address: Address } | null> => {
  const coordinates = { lat: lngLat.lat, lng: lngLat.lng };
  const [place] = features;
  if (place) {
    return {
      name: place.place_name,
      address: {
        country: getContext('country', place),
        region: getContext('region', place),
        city: getContext('place', place),
        postcode: getContext('postcode', place),
        street: place.text || 'Unknown',
        houseNumber: place.address || 'Unknown',
        coordinates,
      },
    };
  }
  if (!allowDistrict) return null;
  const [labels, municipality, area] = await Promise.all([
    settleMapLookup(fetchPlaceLabels(lngLat, HERE_MAX_M)),
    settleMapLookup(fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lngLat.lng},${lngLat.lat}.json?access_token=${accessToken}&types=place`,
    )
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => data?.features?.[0] ?? null)
      .catch(() => null)),
    settleMapLookup(resolveArea(lngLat.lat, lngLat.lng)),
  ]);
  const [district] = pickDistrictRows(labels ?? []);
  const city = area?.name || municipality?.text || '';
  const name = district?.name || city;
  if (!name) return null;
  return {
    name,
    address: {
      country: getContext('country', municipality ?? {}, ''),
      region: getContext('region', municipality ?? {}, ''),
      city,
      postcode: '',
      street: '',
      houseNumber: '',
      coordinates,
    },
  };
};

/**
 * Reverse geocodes the click point and selects the building with the geocoder's
 * first address, as master does. When the geocoder has none, `allowDistrict`
 * (plain buildings, dots and cold links) selects it district-only instead, see
 * resolveClickAddress (JAA-417); otherwise, and when the geocode request
 * fails, nothing is selected. By default the building is selected once, after
 * the POI tilequery. With `selectOnAddress` (the explore preview) it is also
 * selected as soon as the address resolves, so the preview does not wait for
 * the POI round trip; the later call is then a same-building update.
 */
export const performReverseGeocodingAndFetchPOIs = async (
  building: BuildingWithConvertedAddresses,
  selectBuilding: (building: BuildingWithConvertedAddresses) => void,
  lngLat: { lat: number; lng: number },
  selectOnAddress = false,
  allowDistrict = false,
) => {
  // Now perform reverse geocoding
  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

  fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${lngLat.lng},${lngLat.lat}.json?access_token=${accessToken}&types=address`,
  )
    .then((response) => {
      // A failed geocode (rate limit, bad token) must not read as "no address" and open district-only.
      if (!response.ok) throw new Error(`Reverse geocoding failed: ${response.status}`);
      return response.json();
    })
    .then(async (data) => {
      const resolved = await resolveClickAddress(
        data.features ?? [],
        lngLat,
        accessToken,
        allowDistrict,
      );
      if (resolved) {
        const { name, address } = resolved;
        building.properties.name = name;

        building.properties.addresses.push(address);
        building.addresses.push(address);
        if (selectOnAddress) selectBuilding(building);

        // Now fetch POIs using the Tilequery API
        const tilesetId = 'mapbox.mapbox-streets-v8';
        const radius = 500; // in meters
        const layers =
          'poi_label,airport_label,transit_stop_label,ferry_aerialway_label,golf_hole_label,path_pedestrian_label,admin_0_boundary_disputed,admin_0_boundary,admin_1_boundary,admin_0_boundary_bg,aerialway,bridge_rail_tracks,bridge_rail,bridge_pedestrian,bridge_steps,bridge_path,bridge_pedestrian_case,bridge_steps_bg,bridge_path_bg,road_rail_tracks,road_rail,ferry_auto,ferry,golf_hole_line,road_pedestrian,road_steps,road_path,road_pedestrian_case,road_steps,road_path_bg'; // Layer containing POIs
        const limit = 15;

        settleMapLookup(fetch(
          `https://api.mapbox.com/v4/${tilesetId}/tilequery/${lngLat.lng},${lngLat.lat}.json?radius=${radius}&layers=${layers}&limit=${limit}&access_token=${accessToken}`,
        )
          .then((response) => response.json()))
          .then((tilequeryData) => {
            const pois: POI[] = (tilequeryData?.features ?? []).map((feature: any) => {
              const properties = feature.properties;
              return {
                id: feature.id.toString(),
                type: feature.type,
                properties: {
                  name: properties.name || properties.ref || 'Unknown',
                  category: properties.class || properties.type || 'Unknown',
                  layer: properties.tilequery.layer,
                  distanceInMeters: properties.tilequery.distance,
                },
                geometry: feature.geometry,
              };
            });

            // Add POIs to the building object
            building.pois = pois;
            console.log('building', building);
            // Now select the building with POIs
            selectBuilding(building);
          })
          .catch((error) => {
            console.error('Error fetching POIs:', error);
            // Select the building even if POI fetching fails
            selectBuilding(building);
          });
      } else {
        console.log('No address found for this location');
      }
    })
    .catch((error) => {
      console.error('Error with reverse geocoding request:', error);
    });
};
